import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sidebar, 
  SidebarContent, 
  SidebarGroup, 
  SidebarHeader, 
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem 
} from '@/components/ui/sidebar';
import { 
  Home, 
  Bell, 
  MessageCircle, 
  Users, 
  Folder, 
  Calendar as CalendarIcon, 
  FileText, 
  User, 
  Settings, 
  LogOut 
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import NotificationBell from "@/components/NotificationBell";

interface MenuItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href: string;
  roles?: ('user' | 'admin' | 'superadmin' | 'client')[];
  forSaaSOnly?: boolean;
}

const Navigation: React.FC = () => {
  const { user, profile, signOut, isLoading, company, getFeatureAccess } = useAuth();
  const navigate = useNavigate();

  if (isLoading || !user || !profile) {
    return (
      <Sidebar className="border-r border-border">
        <SidebarHeader className="p-4">
          <div className="text-muted-foreground">Loading...</div>
        </SidebarHeader>
        <SidebarContent>
          <div className="p-4 text-muted-foreground text-sm">Authenticating...</div>
        </SidebarContent>
      </Sidebar>
    );
  }

  const safeCompany = company || {
    name: 'WightWay Connect',
    subdomain: '',
    logo_url: null,
    primary_color: '#3b82f6',
    secondary_color: '#6b7280',
    status: 'active',
    created_at: new Date().toISOString(),
  };

  const companyName = safeCompany.name;

  const isClient = profile.role === 'client';
  const isIBPConsulting = getFeatureAccess('ibp_consulting');
  const isSuperAdmin = profile.role === 'superadmin';

  const allPossibleMenuItems: MenuItem[] = [
    { icon: Home, label: 'Dashboard', href: '/dashboard', roles: ['user', 'admin', 'superadmin'] },
    { icon: Bell, label: 'Notifications', href: '/dashboard/notifications', roles: ['user', 'admin', 'superadmin', 'client'] },
    { icon: MessageCircle, label: 'Messages', href: '/dashboard/messages', roles: ['user', 'admin', 'superadmin'] },
    { icon: User, label: 'Profile', href: '/dashboard/profile', roles: ['user', 'admin', 'superadmin', 'client'] },
    { icon: Users, label: 'Contacts', href: '/dashboard/contacts', roles: ['user', 'admin', 'superadmin'], forSaaSOnly: true },
    { icon: Users, label: 'Clients', href: '/dashboard/clients', roles: ['user', 'admin', 'superadmin'], forSaaSOnly: true },
    { icon: Folder, label: 'Projects', href: '/dashboard/projects', roles: ['user', 'admin', 'superadmin'], forSaaSOnly: true },
    { icon: CalendarIcon, label: 'Calendar', href: '/dashboard/calendar', roles: ['user', 'admin', 'superadmin'], forSaaSOnly: true },
    { icon: FileText, label: 'Documents', href: '/dashboard/documents', roles: ['user', 'admin', 'superadmin'], forSaaSOnly: true },
    { icon: FileText, label: 'Tasks', href: '/dashboard/tasks', roles: ['user', 'admin', 'superadmin'], forSaaSOnly: true },
    { icon: CalendarIcon, label: 'Time Tracking', href: '/dashboard/time-tracking', roles: ['user', 'admin', 'superadmin'], forSaaSOnly: true },
    { icon: FileText, label: 'Invoices', href: '/dashboard/invoices', roles: ['user', 'admin', 'superadmin'], forSaaSOnly: true },
    { icon: FileText, label: 'Quotes', href: '/dashboard/quotes', roles: ['user', 'admin', 'superadmin'], forSaaSOnly: true },
    { icon: Settings, label: 'Billing', href: '/dashboard/billing', roles: ['user', 'admin', 'superadmin'], forSaaSOnly: true },
    { icon: Settings, label: 'Admin', href: '/dashboard/admin', roles: ['admin', 'superadmin'] },
    { icon: Home, label: 'Client Portal', href: '/client-portal', roles: ['client'] },
    { icon: Bell, label: 'Notifications', href: '/client-portal/notifications', roles: ['client'] },
    { icon: User, label: 'Profile', href: '/client-portal/profile', roles: ['client'] },
  ];

  // Filter menu items based on role and access
  const menuItems = allPossibleMenuItems.filter(item => {
    // Check if user has required role
    if (item.roles && !item.roles.includes(profile.role)) {
      return false;
    }
    
    // For superadmin, show all items regardless of SaaS-only flag
    if (isSuperAdmin) {
      return true;
    }
    
    // For regular users, check SaaS-only flag
    if (item.forSaaSOnly && isIBPConsulting) {
      return false;
    }
    
    // Client-specific filtering
    if (isClient) {
      return item.href.startsWith('/client-portal');
    } else {
      return !item.href.startsWith('/client-portal');
    }
  });

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleNavigation = (href: string) => {
    navigate(href);
  };

  return (
    <Sidebar className="border-r border-border">
      <SidebarHeader className="p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-foreground">
            {companyName}
          </h2>
          <div className="flex items-center gap-2">
            <NotificationBell />
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton onClick={() => handleNavigation(item.href)}>
                    <Icon className="mr-3 h-4 w-4" />
                    <span>{item.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
        <SidebarGroup className="mt-auto">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={handleSignOut}>
                <LogOut className="mr-3 h-4 w-4" />
                <span>Logout</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};

export { Navigation };